import { ContentCopy, Update } from '@mui/icons-material';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteContent } from '~/api/content';
import { useCopyContentItem, useUpgradeContentItem } from '~/api/content-items';
import type { AuthorizationResult } from '~/api/fragments.generated';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { MoreAction, MoreDeleteAction } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import type { MoreActionProps, MoreMenuProps } from '~/hooks/table';
import type { AppShow__ContentItem as UserApp } from '../gql';

export interface AppFormMenuProps {
  /** Absent for an app that hasn't been saved yet. */
  canDestroy?: AuthorizationResult;
  canUpgrade: boolean;
  id: number;
  moreActionProps: MoreActionProps;
  moreMenuProps: MoreMenuProps<UserApp>;
}

export const AppFormMenu = ({
  canDestroy,
  canUpgrade,
  id,
  moreActionProps,
  moreMenuProps,
}: AppFormMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [deleteConfirm, deleteConfirmDialogProps] = useConfirmDialog();
  const [upgradeConfirm, upgradeConfirmDialogProps] = useConfirmDialog();

  const [upgradeApp] = useUpgradeContentItem();
  const [deleteContent] = useDeleteContent();
  const [copyApp] = useCopyContentItem();

  const onUpgradeApp = useCallback(async () => {
    if (!(await upgradeConfirm())) return;
    await upgradeApp({ variables: { contentItemId: id } });
  }, [id, upgradeApp, upgradeConfirm]);

  const onCopyApp = useCallback(async () => {
    const { data } = await copyApp({ variables: { contentItemId: id } });
    const contentItemId = data?.copyContentItem?.contentItem?.id;
    if (contentItemId != null) navigate(`../../${contentItemId}/settings`, { relative: 'path' });
  }, [copyApp, id, navigate]);

  const onDeleteApp = useCallback(async () => {
    if (!(await deleteConfirm())) return;
    const { data } = await deleteContent({
      variables: { items: [{ contentableId: id, contentableType: 'APP' }] },
    });
    if (data?.deleteContent?.deletedItems.length) navigate('../..');
  }, [deleteConfirm, deleteContent, id, navigate]);

  return (
    <>
      <Menu
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        {...moreMenuProps}
      >
        <MoreAction
          {...moreActionProps}
          Icon={Update}
          disabled={!canUpgrade}
          title={t('pages.content.menu.upgrade')}
          onClick={() => {
            moreMenuProps.onClose();
            void onUpgradeApp();
          }}
        />
        <MenuItem
          onClick={() => {
            void onCopyApp();
            moreMenuProps.onClose();
          }}
        >
          <ListItemIcon>
            <ContentCopy />
          </ListItemIcon>
          <ListItemText>{t('pages.content.menu.duplicate')}</ListItemText>
        </MenuItem>

        <Divider />
        <MoreDeleteAction
          {...moreActionProps}
          disabled={canDestroy?.value === false}
          tooltip={canDestroy?.reasons?.fullMessages.join(', ') ?? ''}
          onClick={() => {
            void onDeleteApp();
            moreMenuProps.onClose();
          }}
        />
      </Menu>

      <ConfirmDialog
        {...deleteConfirmDialogProps}
        confirm={t('common.dialogs.permanently_delete')}
        deleteConfirm
        prompt={t('pages.content.dialogs.delete_app.prompt')}
        title={t('pages.content.dialogs.delete_app.title')}
      />

      <ConfirmDialog
        {...upgradeConfirmDialogProps}
        title={t('pages.content.dialogs.upgrade_app.title')}
        prompt={t('pages.content.dialogs.upgrade_app.prompt')}
        confirm={t('pages.content.dialogs.upgrade_app.confirm')}
      />
    </>
  );
};
